import {
  ColumnValue,
  EGroupType,
  IAccountSummary,
  IExpandedRows,
  IReportConfiguration,
  isCalculationExpression,
  isMultiHierarchyCalculatedRowGroup
} from '@opengov/reporting-types';
import { ColDef, GridApi, RowNode } from 'ag-grid-community';
import { buildMultiHierarchyParentRowId, getGroupingValues } from '../editor/sidepanel/rowEditor/multigroupingUtils';
import {
  AG_GRID_NEW_COLUMN_PLACEHOLDER_ID,
  AG_GRID_NEW_ROW_PLACEHOLDER_ID,
  getCurrentAgGridContext
} from './agGridConsts';
import { QueryContext, getChildData, getRowData } from './getRowData';
import { isMultiHierarchyDataRowSource, MultiGroupingOverflowError, ParentRowsAgGrid } from './getRowDataUtils';
import { getChildData as getChildDataWithCache, getRowData as getRowDataWithCache } from './getRowDataWithCache';
import * as api from '../../api/reportingApi';
import { ExtendedParentNode, ExtendedParams, ServerSideDatasourceArgs } from './serverSideDataSource.types';

export const ROW_GROUP_INTERNAL_NAME = 'row_group';

interface getColumDefsFromReport {
  defaultGroupName?: string;
  rowGroups: IAccountSummary['rowGroups'];
  agGridColumns?: ColDef[];
}

// Multi-hierarchy trees have dynamic depth determined at runtime by the backend
// (via hasChildren metadata), so the static grouping.length no longer applies.
// max depth is set to a high number since `hasChildren` is the source of truth for the depth.
const MULTI_HIERARCHY_MAX_DEPTH = 20;

export function getRowGroupTreeDefs(rowGroups: IAccountSummary['rowGroups']) {
  // ERP override rows count too: they drill to runtime-determined depth exactly like plain
  // multi-hierarchy rows, so a report whose only data rows are override rows still needs the full
  // set of tree colDefs. Sizing it from grouping.length instead would truncate the tree and make
  // drilling past the configured levels render nothing.
  const hasMultiHierarchy = rowGroups.some(isMultiHierarchyDataRowSource);

  const maxRowGroupingDepth = rowGroups.reduce((maxLength, rowGroup) => {
    const isCalculation = isCalculationExpression(rowGroup) || isMultiHierarchyCalculatedRowGroup(rowGroup);
    if (isCalculation) {
      return maxLength;
    }
    const groupLength = rowGroup?.grouping?.length ?? 0;
    return groupLength > maxLength ? groupLength : maxLength;
  }, 0);

  const effectiveDepth = hasMultiHierarchy ? MULTI_HIERARCHY_MAX_DEPTH : maxRowGroupingDepth;

  return Array(effectiveDepth)
    .fill({ hide: true, rowGroup: true })
    .map((defaults, index) => ({
      field: `${ROW_GROUP_INTERNAL_NAME}_${index + 1}`,
      ...defaults
    }));
}

export function getColumnDefsFromReport({
  defaultGroupName = '<defaultGroupName>',
  rowGroups,
  agGridColumns
}: getColumDefsFromReport) {
  const colDefs = [
    {
      field: `${ROW_GROUP_INTERNAL_NAME}_0`,
      hide: true,
      rowGroup: true
    },
    ...getRowGroupTreeDefs(rowGroups),
    ...(agGridColumns ?? []),
    {
      headerName: defaultGroupName,
      children: [
        {
          colId: AG_GRID_NEW_COLUMN_PLACEHOLDER_ID
        }
      ]
    }
  ];

  return colDefs.map((columnDef) => ({
    ...columnDef,
    suppressMovable: false,
    children: columnDef.children?.map((c) => ({ ...c, suppressMovable: false }))
  }));
}

/**
 * Ids of the delivered parent rows whose values just arrived while the grid still holds them as
 * pending. The grid applies a later delivery to an existing row with `updateData`, which re-renders
 * a cell only when its value changed — so a row group whose response has no values (every account
 * filtered out) would keep the loading cells chosen while it was pending, as nothing in its cells
 * changes from `undefined`. Those rows are refreshed by force so their cells pick a renderer again.
 */
function rowIdsNoLongerPending(api: GridApi, rootData: ParentRowsAgGrid[]): string[] {
  const isHeldAsPending = (row: ParentRowsAgGrid) => {
    if (row.id === undefined) return false;
    const held: unknown = api.getRowNode(row.id)?.data;
    return (held as ParentRowsAgGrid | undefined)?.pendingValues === true;
  };
  return rootData.filter((row) => !row.pendingValues && isHeldAsPending(row)).map((row) => row.id as string);
}

function refreshRowsNoLongerPending(api: GridApi, rowIds: string[]) {
  const rowNodes = rowIds.map((id) => api.getRowNode(id)).filter(isTruthy);
  if (rowNodes.length === 0) return;
  api.refreshCells({ rowNodes, force: true, suppressFlash: true });
}

async function getRowsWithCache(args: ServerSideDatasourceArgs, context: QueryContext, params: ExtendedParams) {
  const { defaultRowGroupName, onError, collapseRowGroup } = args;
  const groupingValuesMap = new WeakMap<object, string[]>();
  const { report, columnValues, reportConfigurationId, columnGroups, columnDefinitions } =
    getCurrentAgGridContext(params);

  function handleError(error: unknown) {
    params.fail();
    onError(error);
  }

  const groupKeys = params.request.groupKeys;
  const { dataUpdatedAt, updatedAt } = params.context.get();
  const updatedAtCandidates = [dataUpdatedAt, updatedAt].filter(Boolean) as string[];
  const mostRecentUpdatedAt = updatedAtCandidates.reduce((a, b) => (new Date(a) > new Date(b) ? a : b), '');

  if (!groupKeys || groupKeys.length === 0) {
    try {
      await getRowDataWithCache(
        context,
        report,
        reportConfigurationId,
        columnValues,
        mostRecentUpdatedAt,
        columnGroups,
        columnDefinitions,
        (rootData: ParentRowsAgGrid[]) => {
          const settledRowIds = rowIdsNoLongerPending(params.api, rootData);
          params.success({
            rowData: [
              ...rootData,
              {
                row_group_0: defaultRowGroupName,
                id: AG_GRID_NEW_ROW_PLACEHOLDER_ID,
                placeholder: true,
                values: []
              }
            ],
            rowCount: rootData.length + 1
          });
          refreshRowsNoLongerPending(params.api, settledRowIds);
          const node = params.api.getRowNode(AG_GRID_NEW_ROW_PLACEHOLDER_ID);
          if (node) {
            node.setRowHeight(0);
            node.setSelected(false);
            params.api.onRowHeightChanged();
          }
        }
      );
    } catch (err) {
      console.error(err);
      handleError(err);
    }
    return;
  }

  try {
    const { startRow = 0, endRow } = params?.request ?? {};
    const offset = startRow;
    const limit = endRow ? endRow - offset : NaN;

    if (!groupingValuesMap.has(params.parentNode)) {
      groupingValuesMap.set(params.parentNode, getGroupingValues(params.parentNode as unknown as RowNode));
    }

    const groupingValues = groupingValuesMap.get(params.parentNode) || [];

    const { childData, returnedPivotRowCount } = await getChildDataWithCache(
      context,
      report,
      columnValues,
      {
        rowGroupIndex: params.parentNode.data.rowGroupIndex,
        offset,
        limit,
        groupingValues,
        nodeLevel: params.parentNode.level + 1,
        parentId: params.parentNode.id,
        parentRowKey: (params.parentNode as ExtendedParentNode).data?.rowKey,
        reportConfigurationId
      },
      mostRecentUpdatedAt,
      columnGroups,
      columnDefinitions
    );

    // Use raw backend count to detect end-of-page, but report the filtered count
    // so AG Grid doesn't show loading placeholders for rows removed client-side (e.g. $0 suppression).
    const isLastPage = returnedPivotRowCount < limit;
    const last = isLastPage ? offset + childData.length : undefined;

    params.success({ rowData: childData, rowCount: last });
  } catch (error) {
    if (error instanceof MultiGroupingOverflowError) {
      params.parentNode.setExpanded(false);
      collapseRowGroup(getParentNodeId(params.parentNode as ExtendedParentNode));
      params.fail();

      return;
    }
    handleError(error);
  }
}

async function getRowsWithoutCache(args: ServerSideDatasourceArgs, context: QueryContext, params: ExtendedParams) {
  const { defaultRowGroupName, onError, collapseRowGroup } = args;
  const groupingValuesMap = new WeakMap<object, string[]>();
  const { report, columnValues, reportConfigurationId, columnGroups, columnDefinitions } =
    getCurrentAgGridContext(params);

  function handleError(error: unknown) {
    params.fail();
    onError(error);
  }

  const groupKeys = params.request.groupKeys;

  if (!groupKeys || groupKeys.length === 0) {
    try {
      const rootData = await getRowData(
        context,
        report,
        reportConfigurationId,
        columnValues,
        columnGroups,
        columnDefinitions
      );
      params.success({
        rowData: [
          ...rootData,
          {
            row_group_0: defaultRowGroupName,
            id: AG_GRID_NEW_ROW_PLACEHOLDER_ID,
            placeholder: true,
            values: []
          }
        ],
        rowCount: rootData.length + 1
      });
      const node = params.api.getRowNode(AG_GRID_NEW_ROW_PLACEHOLDER_ID);
      if (node) {
        node.setRowHeight(0);
        node.setSelected(false);
        params.api.onRowHeightChanged();
      }
    } catch (err) {
      console.error(err);
      handleError(err);
    }

    return;
  }

  try {
    const { startRow = 0, endRow } = params?.request ?? {};
    const offset = startRow;
    const limit = endRow ? endRow - offset : NaN;
    if (!groupingValuesMap.has(params.parentNode)) {
      groupingValuesMap.set(params.parentNode, getGroupingValues(params.parentNode as unknown as RowNode));
    }

    const groupingValues = groupingValuesMap.get(params.parentNode) || [];

    const { childData, returnedPivotRowCount } = await getChildData(
      context,
      report,
      columnValues,
      {
        rowGroupIndex: params.parentNode.data.rowGroupIndex,
        offset,
        limit,
        groupingValues,
        nodeLevel: params.parentNode.level + 1,
        parentId: params.parentNode.id,
        parentRowKey: (params.parentNode as ExtendedParentNode).data?.rowKey,
        reportConfigurationId
      },
      columnGroups,
      columnDefinitions
    );

    // Use raw backend count to detect end-of-page, but report the filtered count
    // so AG Grid doesn't show loading placeholders for rows removed client-side (e.g. $0 suppression).
    const isLastPage = returnedPivotRowCount < limit;
    const last = isLastPage ? offset + childData.length : undefined;

    params.success({ rowData: childData, rowCount: last });
  } catch (error) {
    if (error instanceof MultiGroupingOverflowError) {
      params.parentNode.setExpanded(false);
      collapseRowGroup(getParentNodeId(params.parentNode as ExtendedParentNode));
      params.fail();

      return;
    }
    handleError(error);
  }
}

export function serverSideDatasource(args: ServerSideDatasourceArgs, context: QueryContext) {
  return {
    async getRows(params: ExtendedParams) {
      const { dataTableError } = getCurrentAgGridContext(params);
      if (dataTableError) {
        // Settle the request: a bare return leaves the block loading, so the grid spins forever.
        params.fail();
        return;
      }

      if (context.gridCacheEnabled) {
        return getRowsWithCache(args, context, params);
      } else {
        return getRowsWithoutCache(args, context, params);
      }
    },
    entitySubdomain: context.entitySubdomain
  };
}

// makes typescript work with [].filter(Boolean)
export function isTruthy<T>(value?: T | null | false): value is T {
  return !!value;
}

/**
 * Recursively traverse the expanded rows in order to identify the
 * child rows that need to be prefetched. This is done prior
 * to the initial render of the grid and is used to hydrate the cache.
 */

type CommonRowRequestPayload = {
  report: IAccountSummary;
  rowIndex: number;
  columnValues: ColumnValue[];
  entitySubdomain: string;
  entityFiscalYearStartMonth: number;
  reportConfigurationId: string;
  /** Set only for a combined report, from the loaded report's `entityGroupId`. */
  entityGroupId?: string;
};

export type RowRequestPayload =
  | {
      variant: 'parent';
      payload: CommonRowRequestPayload & {
        parentQuery: true;
      };
    }
  | {
      variant: 'child';
      payload: CommonRowRequestPayload & {
        parentQuery: false;
        limit: number;
        offset: number;
        groupingValues: string[];
      };
    };

export function prepareRowRequestsForPrefetching({
  reportConfiguration,
  columnValues,
  entitySubdomain,
  entityFiscalYearStartMonth,
  entityGroupId
}: {
  reportConfiguration: IReportConfiguration;
  columnValues: ColumnValue[];
  entitySubdomain: string;
  entityFiscalYearStartMonth: number;
  /** Set only for a combined report, from the loaded report's `entityGroupId`. */
  entityGroupId?: string;
}) {
  const accountSummary = reportConfiguration.accountSummary as IAccountSummary;
  const rowGroups = accountSummary.rowGroups;
  const expandedRows = accountSummary.expandedRows;
  const rows: RowRequestPayload[] = [];

  const commonPayload = (rowIndex: number) => ({
    report: accountSummary,
    rowIndex,
    columnValues,
    entitySubdomain,
    entityFiscalYearStartMonth,
    reportConfigurationId: reportConfiguration.id,
    entityGroupId
  });

  rowGroups.forEach((rowGroup, rowIndex) => {
    // Calculated rows are computed via /pivot/rows/calculate, not prefetched via /pivot/rows
    if (rowGroup.type === EGroupType.MultiHierarchyCalculated) {
      return;
    }
    const { id } = rowGroup;
    // Override rows store their expanded state under the same `<rowGroupId>:<groupId>` composite
    // keys as plain multi-hierarchy rows, so they must take the composite branch below. The legacy
    // `expandedRows[id]` branch would never match, silently prefetching none of their children.
    const isMultiHierarchy = isMultiHierarchyDataRowSource(rowGroup);

    const parentRow = {
      variant: 'parent' as const,
      payload: {
        ...commonPayload(rowIndex),
        parentQuery: true as const
      }
    };

    rows.push(parentRow);

    if (isMultiHierarchy && id) {
      // For multi-hierarchy, each groupId in grouping[0].groupIds may have its own
      // expanded state stored under the <rowGroupId>:<groupId> composite key.
      const multiHierarchyRowGroup = rowGroup;
      const groupIds = multiHierarchyRowGroup.grouping.length > 0 ? multiHierarchyRowGroup.grouping[0].groupIds : [];

      const traverse = (node: IExpandedRows, path: string[]) => {
        Object.keys(node).forEach((key) => {
          const newPath = [...path, key];
          rows.push({
            variant: 'child',
            payload: {
              ...commonPayload(rowIndex),
              limit: 1000,
              offset: 0,
              groupingValues: newPath,
              parentQuery: false as const
            }
          });
          traverse(node[key], newPath);
        });
      };

      groupIds.forEach((groupId) => {
        const parentRowId = buildMultiHierarchyParentRowId(id, groupId);
        if (expandedRows[parentRowId]) {
          // The backend reads rowAncestorId from groupingValues[0] for child queries.
          const rootGroupingValues = [groupId];
          rows.push({
            variant: 'child',
            payload: {
              ...commonPayload(rowIndex),
              limit: 1000, // matches the limit used by ag-grid triggered row requests
              offset: 0,
              groupingValues: rootGroupingValues,
              parentQuery: false as const
            }
          });
          traverse(expandedRows[parentRowId], rootGroupingValues);
        }
      });
    } else if (id && expandedRows[id]) {
      // Non-multi-hierarchy: root grouping values are empty
      const rootGroupingValues: string[] = [];

      rows.push({
        variant: 'child',
        payload: {
          ...commonPayload(rowIndex),
          limit: 1000, // matches the limit used by ag-grid triggered row requests
          offset: 0,
          groupingValues: rootGroupingValues,
          parentQuery: false as const
        }
      });

      // recurse through expanded rows and keep track of the path. The path is the column groups
      const traverse = (node: IExpandedRows, path: string[]) => {
        Object.keys(node).forEach((key) => {
          const newPath = [...path, key];
          rows.push({
            variant: 'child',
            payload: {
              ...commonPayload(rowIndex),
              limit: 1000,
              offset: 0,
              groupingValues: newPath,
              parentQuery: false as const
            }
          });
          traverse(node[key], newPath);
        });
      };

      traverse(expandedRows[id], rootGroupingValues);
    }
  });

  return rows;
}

/**
 * Calculates all the row requests that need to be prefetched based
 * on which rows are initially expanded in ag-grid.
 */

export function getInitiallyExpandedRowRequests({
  reportConfiguration,
  entitySubdomain,
  entityFiscalYearStartMonth,
  columnValues,
  maxRequests,
  expireCacheIfOlderThan,
  entityGroupId
}: {
  reportConfiguration: IReportConfiguration;
  entitySubdomain: string;
  entityFiscalYearStartMonth: number;
  columnValues: ColumnValue[];
  maxRequests: number;
  expireCacheIfOlderThan: string;
  /** Set only for a combined report, from the loaded report's `entityGroupId`. */
  entityGroupId?: string;
}) {
  const rowRequestPayloads = prepareRowRequestsForPrefetching({
    reportConfiguration,
    columnValues,
    entitySubdomain,
    entityFiscalYearStartMonth,
    entityGroupId
  });

  const limitedRequests = rowRequestPayloads.slice(0, maxRequests);

  const rowRequests = limitedRequests.map((row) => {
    return api.pivotRows(row.payload, { cache: true, expireIfOlderThan: expireCacheIfOlderThan });
  });

  return rowRequests;
}

export function getParentNodeId(node: ExtendedParentNode): string {
  return node.data?.rowKey || node.id || '';
}
